import React, { useState } from 'react';
import { ShieldIcon, NetworkIcon, LightbulbIcon, CheckCircleIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { AccordionItem } from '../components/AccordionItem';
import { PageHero } from '../components/PageHero';
import { ProcessTimeline } from '../components/ProcessTimeline';
import { CTASection } from '../components/CTASection';
import { Button } from '../components/Button';
interface WoonbotenPageProps {
  onNavigate: (page: string) => void;
}
export function WoonbotenPage({
  onNavigate
}: WoonbotenPageProps) {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const faqs = [{
    question: 'Welke banken financieren woonboten?',
    answer: 'Niet elke reguliere bank financiert woonboten, omdat het risicoprofiel anders is dan bij een stenen huis. Wij hebben een specifiek netwerk van gespecialiseerde geldverstrekkers die hier wél ervaring mee hebben. Wij weten precies waar we jouw aanvraag met de grootste kans op succes kunnen indienen.'
  }, {
    question: 'Wat zijn de specifieke eisen voor een woonboothypotheek?',
    answer: 'Er gelden vaak aanvullende eisen. Zo moet de woonboot een vaste ligplaats hebben met een vergunning, ingeschreven staan in het scheepsregister van het Kadaster, en er is vaak een recent taxatierapport van een gespecialiseerde woonboottaxateur nodig. Wij begeleiden je bij al deze specifieke stappen.'
  }, {
    question: 'Hoe verschilt een woonboothypotheek van een reguliere hypotheek?',
    answer: 'De rentetarieven liggen vaak iets hoger door een kleine toeslag, en het maximale leenbedrag ten opzichte van de woningwaarde kan afwijken. Ook de notariële afhandeling is specifieker. Omdat wij deze niche door en door kennen, kom je niet voor verrassingen te staan.'
  }, {
    question: 'Wat kost een woonboothypotheek?',
    answer: 'De rentetarieven voor woonboten liggen gemiddeld 0,5% tot 1,5% hoger dan bij een reguliere hypotheek, afhankelijk van de geldverstrekker en het type woonboot. Tijdens een vrijblijvend gesprek berekenen wij precies wat de maandlasten in jouw situatie worden.'
  }, {
    question: 'Kan ik een woonboot kopen in Amsterdam?',
    answer: 'Zeker. Amsterdam heeft veel woonboten, maar de ligplaatsvergunningen zijn schaars en waardevol. Wij kennen de specifieke regels per stadsdeel en weten welke geldverstrekkers ervaring hebben met Amsterdamse woonboten. Of het nu gaat om de Amstel, de Prinsengracht of de Schinkel, wij helpen je verder.'
  }, {
    question: 'Heb ik een taxatie nodig voor mijn woonboot?',
    answer: 'Ja, je hebt een taxatierapport nodig van een gespecialiseerde woonboottaxateur. Dit is anders dan een reguliere woningtaxatie. De taxateur beoordeelt onder andere de staat van de romp, de ligplaats en de vergunningssituatie. Wij kunnen je doorverwijzen naar betrouwbare woonboottaxateurs in de regio.'
  }];
  const timelineSteps = [{
    number: 1,
    title: 'Haalbaarheid',
    description: 'Wij checken direct de haalbaarheid op basis van de specifieke woonboot, ligplaats en jouw financiële situatie.'
  }, {
    number: 2,
    title: 'Dossier opbouwen',
    description: 'Wij verzamelen samen met jou de juiste documenten, zoals een gespecialiseerd taxatierapport en bewijs van inschrijving in het scheepsregister.'
  }, {
    number: 3,
    title: 'Financiering rond',
    description: 'Wij dienen de aanvraag in bij de juiste bank en begeleiden het traject tot aan de notaris.'
  }];
  return <div className="w-full" data-id="element-846">
      {/* Hero */}
      <PageHero variant="default" title="Dé specialist in woonboothypotheken" subtitle="De meeste banken zeggen 'nee' tegen woonboten. Wij hebben het netwerk en de kennis om jouw financiering wél rond te krijgen." label="Woonboten" data-id="element-847" />

      {/* Waarom JDG voor woonboten — merged & enriched */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-848">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-849">
          <SectionHeading title="Waarom JDG voor je woonboot?" subtitle="Een unieke woning vraagt om unieke kennis. Het financieren van een woonboot is een vak apart. Wij nemen de complexiteit uit handen." data-id="element-850" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-851">
            <FadeIn delay={0.1} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-852">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-853">
                <NetworkIcon className="w-7 h-7 text-gold" data-id="element-854" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-855">
                Specialist netwerk
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-856">
                De meeste reguliere banken financieren geen woonboten. Wij
                werken samen met een selecte groep gespecialiseerde
                geldverstrekkers en weten precies bij welke bank jouw aanvraag
                de grootste kans van slagen heeft.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-857">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-858">
                <CheckCircleIcon className="w-7 h-7 text-gold" data-id="element-859" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-860">
                Jarenlange ervaring
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-861">
                Bij een woonboothypotheek gelden andere regels: hogere rente,
                specifieke taxaties, scheepsregistratie en
                ligplaatsvergunningen. Wij kennen alle valkuilen en begeleiden
                je veilig door het proces.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-white p-8 rounded-2xl shadow-card border border-gray-100" data-id="element-862">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6" data-id="element-863">
                <LightbulbIcon className="w-7 h-7 text-gold" data-id="element-864" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-3" data-id="element-865">
                Creatieve oplossingen
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-866">
                Lijkt de financiering vast te lopen? Wij denken in oplossingen.
                Ook voor de verzekering van je woonboot hebben wij de juiste
                connecties om dit goed en betaalbaar te regelen.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <ProcessTimeline title="Het proces in 3 stappen" steps={timelineSteps} data-id="element-867" />

      {/* CTA after timeline */}
      <section className="pb-20 md:pb-28 bg-warm" data-id="element-868">
        <div className="text-center" data-id="element-869">
          <FadeIn data-id="element-870">
            <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} data-id="element-871">
              Plan een gratis kennismaking
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Insurance Cross-sell */}
      <section className="py-20 md:py-28 bg-navy text-white" data-id="element-872">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-873">
          <FadeIn className="flex flex-col md:flex-row items-center gap-8 text-center md:text-left" data-id="element-874">
            <div className="w-14 h-14 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-875">
              <ShieldIcon className="w-7 h-7 text-gold" data-id="element-876" />
            </div>
            <div data-id="element-877">
              <h3 className="text-2xl font-bold text-gold mb-3" data-id="element-878">
                Vergeet de woonbootverzekering niet
              </h3>
              <p className="text-gray-300 leading-relaxed mb-6" data-id="element-879">
                Naast de financiering is ook het verzekeren van een woonboot
                maatwerk. Wij hebben de juiste connecties voor een passende
                opstal- en inboedelverzekering voor jouw schip.
              </p>
              <Button variant="ghost" onClick={() => onNavigate('verzekeringen')} className="text-white hover:text-gold" data-id="element-880">
                Bekijk onze verzekeringen
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 md:py-28 bg-white" data-id="element-881">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-882">
          <SectionHeading title="Veelgestelde vragen" subtitle="Alles wat je moet weten over het financieren van een woonboot." data-id="element-883" />

          <FadeIn delay={0.2} className="bg-warm rounded-2xl shadow-card p-6 md:p-10 border border-gray-100" data-id="element-884">
            {faqs.map((faq, index) => <AccordionItem key={index} question={faq.question} answer={faq.answer} isOpen={openAccordion === index} onClick={() => setOpenAccordion(openAccordion === index ? null : index)} data-id="element-885" />)}
          </FadeIn>

          <FadeIn delay={0.4} className="mt-16 text-center" data-id="element-886">
            <p className="text-navy font-medium mb-6" data-id="element-887">
              Staat jouw vraag er niet tussen?
            </p>
            <Button variant="primary" size="lg" onClick={() => onNavigate('contact')} data-id="element-888">
              Stel je vraag
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* Cross-page CTA */}
      <CTASection title="Liever een reguliere hypotheek?" subtitle="Wij helpen je ook graag met een standaard hypotheek. Bekijk ons hypotheekadvies." buttonText="Bekijk ons hypotheekadvies" buttonVariant="secondary" onNavigate={onNavigate} page="hypotheken" variant="light" data-id="element-889" />
    </div>;
}